import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The "shell" half of the Gallery desktop master-detail behavior. Owns
 * `isDesktop`, `selectedId`, `openInline`/`closeInline`, stale-selection
 * cleanup, and scroll-into-view on selection. Safe to call BEFORE the
 * filtered/paged data is available.
 *
 * `closeInlineRef` is exposed so the pagination-scroll effect in the parent
 * can call the latest `closeInline` without capturing an old identity.
 *
 * Behavior-preserving extraction from Gallery.jsx (iter56).
 */
export function useGalleryDesktopMasterDetail({
  searchParams,
  setSearchParams,
  items,
}) {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const selectedId = isDesktop ? searchParams.get("selected") : null;

  // Route URL writes through a stable ref so `openInline`/`closeInline`
  // don't get a new identity every time react-router-dom v7 mints a new
  // `setSearchParams`. Any effect that depends on these callbacks would
  // otherwise re-run on every unrelated URL write.
  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => { setSearchParamsRef.current = setSearchParams; }, [setSearchParams]);

  // `openInline` also accepts an optional `page` (1-indexed) so callers
  // (Arrow-key nav) can update `selected` + `page` in ONE atomic write.
  // A prior version of this code did two separate `setSearchParams` calls
  // in the same tick; both updaters read the same committed history entry,
  // so the second clobbered the first — which meant Arrow-Right from the
  // last card on a page moved the pane's target but never advanced the
  // grid's `?page`.
  const openInline = useCallback((scanId, nextPageOneIndexed = null) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      next.set("selected", scanId);
      if (nextPageOneIndexed !== null) {
        if (nextPageOneIndexed <= 1) next.delete("page");
        else next.set("page", String(nextPageOneIndexed));
      }
      return next;
    }, { replace: true });
  }, []);

  const closeInline = useCallback(() => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("selected");
      return next;
    }, { replace: true });
  }, []);

  // If the selection references a deleted or missing scan, drop it silently.
  useEffect(() => {
    if (isDesktop && selectedId && items && !items.find((i) => i.scan_id === selectedId)) {
      closeInline();
    }
  }, [isDesktop, selectedId, items, closeInline]);

  // When a card is selected on desktop, scroll it into a comfortable spot in
  // the viewport so the sticky detail pane aligns visually with the clicked
  // card. Without this, tapping a plant near the bottom of the grid leaves
  // the pane at the top of the viewport but the card far below.
  useEffect(() => {
    if (!isDesktop || !selectedId) return;
    // Wait one paint so the pane column has settled the grid re-flow.
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="plant-card-${selectedId}"]`);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      // If the card is already fully visible in the top ~70% of the viewport,
      // leave scroll alone. Otherwise nudge it toward the top so the sticky
      // pane sits alongside it.
      const comfortablyVisible = rect.top >= 16 && rect.bottom <= viewportH * 0.7;
      if (!comfortablyVisible) {
        el.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [isDesktop, selectedId]);

  // Latest-value ref for callers (e.g. pagination-scroll effect) that need to
  // call closeInline without re-running when its identity changes.
  const closeInlineRef = useRef(closeInline);
  useEffect(() => { closeInlineRef.current = closeInline; }, [closeInline]);

  return { isDesktop, selectedId, openInline, closeInline, closeInlineRef };
}

/**
 * Arrow-key + Escape navigation across the currently filtered/sorted list.
 * Must be called AFTER `filtered` and `page`/`pageSize` are available.
 *
 * Behavior-preserving extraction from Gallery.jsx (iter56).
 *
 * `keyboardNavRef`, when provided, is flipped to `true` right before an
 * Arrow-key page jump so the parent's pagination-scroll effect can skip its
 * own `closeInline()` — otherwise the very selection this hook just wrote
 * would be clobbered in the same commit.
 */
export function useGalleryDesktopKeyboardNav({
  isDesktop,
  selectedId,
  filtered,
  page,
  pageSize,
  openInline,
  closeInline,
  keyboardNavRef,
}) {
  useEffect(() => {
    if (!isDesktop || !selectedId || !filtered || filtered.length === 0) return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const isEditable = tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable;
      if (isEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Escape") {
        e.preventDefault();
        closeInline();
        return;
      }
      const dir = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
      if (!dir) return;
      const idx = filtered.findIndex((it) => it.scan_id === selectedId);
      if (idx === -1) return;
      const nextIdx = idx + dir;
      if (nextIdx < 0 || nextIdx >= filtered.length) return; // clamp at ends
      e.preventDefault();
      const nextScan = filtered[nextIdx];
      // Compute the target page (1-indexed) if the next scan crosses a page
      // boundary. `openInline` accepts an optional page so we do ONE atomic
      // URL write instead of two — a second setSearchParams in the same
      // tick would clobber the first (both would read the same committed
      // history entry). This is the fix for the pre-existing "Arrow-Right
      // from last card on a page updates the pane but not the grid" bug.
      const nextPageZero = pageSize > 0 ? Math.floor(nextIdx / pageSize) : 0;
      const nextPageOne = nextPageZero + 1;
      const currentPageOne = page + 1;
      const crossesPage = nextPageOne !== currentPageOne;
      // Tell the parent's pagination-scroll effect: this page change is
      // keyboard-initiated; DON'T auto-close the pane in your effect body,
      // or you'll wipe the selection we just wrote in the same commit.
      if (crossesPage && keyboardNavRef) keyboardNavRef.current = true;
      openInline(nextScan.scan_id, crossesPage ? nextPageOne : null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDesktop, selectedId, filtered, page, pageSize, openInline, closeInline, keyboardNavRef]);
}
